/**
 * Aviator — the TABLE CLOCK seam (plan §7). The Page derives every phase from
 * this interface and nothing else, so the Phase-D swap to the server clock is
 * ONE import: `LocalTableClock` (this file: seeded RNG, practice wallet, the
 * only caller of engine.sampleCrash) → `ServerTableClock` (/time + /state +
 * the flight long-poll). Same method names, same round shape.
 */
import {
  mAt, tFor, payout, sampleCrash, q2,
  WINDOW_MS, ARM_MS, SETTLE_MS, INTERMISSION_MS,
  type PanelState,
} from './engine';
import { mulberry32, mix } from './prng';

export type RoundStatus = 'betting' | 'locked' | 'flying' | 'crashed' | 'intermission';

export type Round = {
  no: number;
  status: RoundStatus;
  opensAt: number;
  locksAt: number;
  takeoffAt: number;
  /** known only once status === 'crashed' (the server never leaks it earlier) */
  crashAt: number | null;
  M: number | null;
  settlesAt: number | null;
  nextOpensAt: number | null;
  fakeSeed: number;
};

export type Panel = 0 | 1;

export interface Wallet {
  balance(): number;
  /** returns false when the purse is short — no partial debits */
  debit(n: number): boolean;
  credit(n: number): void;
}

/**
 * What a CASH OUT answers with.
 *
 * `message` is the server's own refusal sentence, shown raw when there is one — the practice
 * clock never sets it (it has nobody to quote).
 */
export type CashoutResult =
  | { ok: true; m: number; won: number }
  | { ok: false; reason: 'too_late' | 'not_flying'; message?: string };

export interface TableClock {
  serverNow(): number;
  round(): Round;
  /** live multiplier while flying (2 dp), 1 otherwise */
  mNow(): number;
  panel(p: Panel): PanelState;
  history(): number[];                               // last crash points, newest first
  subscribe(cb: () => void): () => void;
  bet(p: Panel, stake: number, autoX: number | null): { ok: true; queued: boolean } | { ok: false; reason: string };
  cancel(p: Panel): boolean;
  /**
   * Take the money mid-flight.
   *
   * SYNCHRONOUS on the practice clock (it owns the crash point and the purse, so the answer is
   * already known); a PROMISE on the server clock, and deliberately so (2026-09-06). What a
   * real cash out pays is priced by `model.cashout` from the DB clock at the instant the
   * request lands — always a touch below what the screen showed. Returning an optimistic
   * multiplier here would put a number on screen, and in the celebration ticker, that the
   * wallet then contradicts. A stake can be shown optimistically and put back if refused; a WIN
   * cannot. So the Page awaits this, and the figure it celebrates is the server's.
   */
  cashout(p: Panel): CashoutResult | Promise<CashoutResult>;
  /**
   * Is the balance on screen the REAL one? (2026-09-06)
   *
   * The practice clock funds itself, so it is true from the first frame. The server clock only
   * learns the player's balance when its first `/state` lands, and until then the Page would
   * otherwise show the seeded practice figure — a number the player does not have. On a
   * real-money table that is a lie, and worse, `bet()` gates affordability on it, so a player
   * could compose a stake the server then refuses for reasons the UI never showed. The Page
   * shows a placeholder and holds betting closed until this goes true.
   */
  walletReady(): boolean;
  destroy(): void;
}

/* ─── the practice clock ─────────────────────────────────────────────────── */

export class LocalTableClock implements TableClock {
  private r: Round;
  private panels: PanelState[] = [{ kind: 'idle' }, { kind: 'idle' }];
  private hist: number[] = [];
  private subs = new Set<() => void>();
  private timer = 0;
  private rng: () => number;

  constructor(private tableSeed: number, private wallet: Wallet, firstRoundNo = 1) {
    this.rng = mulberry32(mix(tableSeed, 0xC1A5));
    /* a believable history before the first round — deterministic per table */
    for (let i = 0; i < 20; i++) this.hist.push(sampleCrash(this.rng));
    this.r = this.open(firstRoundNo, performance.now() + 400);
    this.timer = window.setInterval(() => this.tick(), 100);
  }

  private open(no: number, opensAt: number): Round {
    return {
      no, status: 'betting', opensAt,
      locksAt: opensAt + WINDOW_MS,
      takeoffAt: opensAt + WINDOW_MS + ARM_MS,
      crashAt: null, M: null, settlesAt: null, nextOpensAt: null,
      fakeSeed: mix(this.tableSeed, no),
    };
  }

  serverNow() { return performance.now(); }
  round() { return this.r; }
  panel(p: Panel) { return this.panels[p]; }
  history() { return this.hist; }

  mNow() {
    if (this.r.status !== 'flying') return 1;
    return mAt((this.serverNow() - this.r.takeoffAt) / 1000);
  }

  /** Practice chips are funded locally, so the figure on screen is never a guess. */
  walletReady() { return true; }
  subscribe(cb: () => void) { this.subs.add(cb); return () => { this.subs.delete(cb); }; }
  private emit() { this.subs.forEach((cb) => cb()); }

  /* the hidden truth of the running round — PHASE-2 SEAM: lives in the
     server's result_secret, never on the client */
  private secretM = 1;

  private tick() {
    const now = this.serverNow();
    const r = this.r;
    let changed = false;

    if (r.status === 'betting' && now >= r.locksAt) {
      r.status = 'locked';
      /* the decision happens AT LOCK, exactly like the worker */
      this.secretM = sampleCrash(this.rng);
      changed = true;
    }
    if (r.status === 'locked' && now >= r.takeoffAt) {
      r.status = 'flying';
      this.panels = this.panels.map((s) => (s.kind === 'placed' ? { kind: 'flying', stake: s.stake, autoX: s.autoX } : s));
      changed = true;
    }
    if (r.status === 'flying') {
      const t = (now - r.takeoffAt) / 1000;
      const m = mAt(t);
      /* auto cashouts fire at EXACTLY their X, never at tick time */
      this.panels = this.panels.map((s) => {
        if (s.kind === 'flying' && s.autoX != null && s.autoX < this.secretM && m >= s.autoX) {
          const won = payout(s.stake, s.autoX);
          this.wallet.credit(won);
          changed = true;
          return { kind: 'cashed', stake: s.stake, m: s.autoX, won };
        }
        return s;
      });
      if (t >= tFor(this.secretM)) {
        r.status = 'crashed';
        r.M = this.secretM;
        r.crashAt = r.takeoffAt + tFor(this.secretM) * 1000;
        r.settlesAt = now + SETTLE_MS;
        r.nextOpensAt = now + SETTLE_MS + INTERMISSION_MS;
        this.hist = [this.secretM, ...this.hist].slice(0, 20);
        this.panels = this.panels.map((s) => (s.kind === 'flying' ? { kind: 'lost', stake: s.stake, M: this.secretM } : s));
        changed = true;
      }
    }
    if (r.status === 'crashed' && r.settlesAt != null && now >= r.settlesAt) {
      r.status = 'intermission';
      changed = true;
    }
    if (r.status === 'intermission' && r.nextOpensAt != null && now >= r.nextOpensAt) {
      this.r = this.open(r.no + 1, now);
      /* queued bets become placed; results clear back to idle */
      this.panels = this.panels.map((s) =>
        s.kind === 'queued' ? { kind: 'placed', stake: s.stake, autoX: s.autoX }
        : (s.kind === 'cashed' || s.kind === 'lost') ? { kind: 'idle' } : s);
      changed = true;
    }
    if (changed || r.status === 'flying') this.emit();
  }

  bet(p: Panel, stake: number, autoX: number | null) {
    const s = this.panels[p];
    if (s.kind !== 'idle' && s.kind !== 'cashed' && s.kind !== 'lost') return { ok: false as const, reason: 'busy' };
    if (!this.wallet.debit(stake)) return { ok: false as const, reason: 'balance' };
    /* PHASE-2 SEAM: POST /aviator/bet {roundNo, panel, stake, autoX} — the
       server answers {queuedFor} when the tap lands after lock */
    const queued = this.r.status !== 'betting';
    this.panels[p] = queued
      ? { kind: 'queued', stake, autoX }
      : { kind: 'placed', stake, autoX };
    this.emit();
    return { ok: true as const, queued };
  }

  cancel(p: Panel) {
    const s = this.panels[p];
    if (s.kind !== 'placed' && s.kind !== 'queued') return false;
    this.wallet.credit(s.stake);                       // PHASE-2 SEAM: POST /aviator/unbet
    this.panels[p] = { kind: 'idle' };
    this.emit();
    return true;
  }

  cashout(p: Panel) {
    const s = this.panels[p];
    if (s.kind !== 'flying' || this.r.status !== 'flying') return { ok: false as const, reason: 'not_flying' as const };
    const t = (this.serverNow() - this.r.takeoffAt) / 1000;
    if (t >= tFor(this.secretM)) return { ok: false as const, reason: 'too_late' as const };
    const m = q2(mAt(t));
    const won = payout(s.stake, m);                    // PHASE-2 SEAM: POST /aviator/cashout {betId}
    this.wallet.credit(won);
    this.panels[p] = { kind: 'cashed', stake: s.stake, m, won };
    this.emit();
    return { ok: true as const, m, won };
  }

  destroy() { window.clearInterval(this.timer); this.subs.clear(); }
}
